// pages/FormViewerPage/hooks/useFormViewer/submitPayload.ts — rakit payload order (thin)
import type { Form } from '../../../../types';
import { normalizePhoneForDb } from '../../../../utils/formatPhone';
import { canonPlatform } from '../../../../services/platforms';
import { canonicalPaymentMethod } from '../../../../utils/paymentMethod';
import { isKnownDuitkuMethod } from '../../../../constants/duitkuFormMethods';
import { assignCs } from '../../services/formViewerService';
import { buildCustomerFields } from './submitCustomer';
import { buildShippingFields } from './submitShipping';
import { getEffectiveMaxSubsidy } from '../../../../utils/shippingSubsidy';

export async function buildOrderPayload(params: {
  form: Form | null;
  customerData: any;
  addressData: any;
  selectedOptions: Record<string, string>;
  selectedShippingKey: any;
  selectedPaymentKey: any;
  selectedDuitkuMethod?: string | null;
  selectedEverproRate: any;
  selectedBiteshipRate: any;
  currentCombination: any;
  linkedProductName: string;
  quantity: number;
  shippingCost: number;
  codFee: number;
  orderId: string;
  uniqueCode: number;
  isCODVal: boolean;
  isSimpleEmbed?: boolean;
  herbalClaimMode?: 'wa' | 'claim_ongkir';
  discountAmount?: number;
  voucherCode?: string;
  // Wizard step 1 menandai order parsial agar step 2-3 bisa melengkapi via PATCH.
  sourceFormOverride?: string;
  bumpProductIds?: string[];
  bumpSum?: number;
}) {
  const {
    form, customerData, addressData, selectedOptions, selectedShippingKey,
    selectedPaymentKey, selectedDuitkuMethod, selectedEverproRate, selectedBiteshipRate, currentCombination, linkedProductName,
    quantity, shippingCost, codFee, orderId, uniqueCode, isCODVal, isSimpleEmbed, herbalClaimMode, discountAmount, voucherCode, sourceFormOverride,
    bumpProductIds, bumpSum,
  } = params;
  // Pembayaran otomatis Duitku: simpan nama metode utuh + gateway (JANGAN lewat
  // canonicalPaymentMethod — 'Duitku QRIS' akan terkunci jadi 'QRIS' dan info gateway hilang).
  const duitkuMethod = selectedPaymentKey === 'duitku' && isKnownDuitkuMethod(selectedDuitkuMethod) ? String(selectedDuitkuMethod) : null;

  const utmParams: Record<string, string> = {};
  const sp = new URLSearchParams(window.location.search);
  sp.forEach((v, k) => { if (k.startsWith('utm_')) utmParams[k] = v; });
  const platformVal = new URLSearchParams(window.location.search).get('platform') || '';
  if (platformVal && !utmParams.utm_source) utmParams.utm_source = platformVal;

  let assignedCsId: string | undefined;
  try { assignedCsId = await assignCs(form); } catch { /* */ }

  const cf = buildCustomerFields(form, customerData, addressData, selectedOptions, linkedProductName, currentCombination, isSimpleEmbed, herbalClaimMode, quantity);
  const { shipMethod, everproFull, discount } = buildShippingFields(form, selectedEverproRate, selectedBiteshipRate, selectedShippingKey, selectedPaymentKey, cf.herbalSubsidy, discountAmount, currentCombination, quantity);
  // OTORITATIF LOKAL (insiden ORD-1792905029070 2026-10-26): JANGAN percaya param
  // shippingCost/codFee mentah bila kebijakan subsidi — hitung ulang pay dari tarif
  // penuh + maks form yang SAMA. Mencegah pay=tarif penuh + subsidy=0 lolos ke RPC
  // (RPC kini juga memvalidasi server-side; ini lapis pertahanan pertama).
  let effShipCost = shippingCost;
  let effCodFee = codFee;
  let effSubsidy = cf.herbalSubsidy > 0 ? cf.herbalSubsidy : (shipMethod === 'everpro (subsidi)' ? Math.max(0, everproFull - shippingCost) : 0);
  if (shipMethod === 'everpro (subsidi)' && everproFull > 0) {
    const maks = getEffectiveMaxSubsidy(form, currentCombination ?? null, quantity);
    if (maks > 0) {
      const pay = Math.max(0, everproFull - Math.round(maks));
      const sub = Math.min(everproFull, Math.round(maks));
      effSubsidy = sub;
      if (effShipCost !== pay) {
        effShipCost = pay;
        // COD ulang dengan basis pay yang benar (paritas computeCodFee).
        try {
          const pk = String(selectedPaymentKey || '');
          const p: any = (form as any)?.paymentSettings?.[pk];
          const isCod = ['cod', 'codJawaBali', 'codLuarJawaBali'].includes(pk);
          if (isCod && p?.visible && p.handlingFeePercentage) {
            const unit = (currentCombination as any)?.sellingPrice || (form as any)?.productPrice || 0;
            const base = p.handlingFeeBase === 'product' ? unit * quantity : unit * quantity + pay;
            effCodFee = Math.round(base * (p.handlingFeePercentage || 0) / 100);
          }
        } catch { /* pertahankan codFee asal bila gagal */ }
      }
    }
  }
  const pOrder: Record<string, any> = {
    id: orderId, formId: form?.id || '', customer: cf.name,
    customerPhone: normalizePhoneForDb(cf.phone), customerEmail: cf.email, productName: cf.productName,
    variant: cf.varName, productPrice: cf.sellingPrice, shippingCost: effShipCost, codFee: effCodFee,
    shippingOriginalCost: selectedEverproRate ? everproFull : effShipCost,
    shippingSubsidy: effSubsidy,
    totalPrice: (cf.sellingPrice * quantity) + (Number(bumpSum) || 0) + effShipCost + effCodFee - discount,
    bumpProductIds: (bumpProductIds && bumpProductIds.length ? bumpProductIds : undefined),
    discountAmount: discount,
    voucherCode: voucherCode || undefined,
    shippingMethod: shipMethod,
    // Nama kurir Everpro ada di `logistic_name` (bukan `courier_name` — itu legacy
    // Biteship; insiden courier='' 2026-10-03 order #88dcc01c). Persist juga
    // signed_key + durasi supaya booking Everpro bisa langsung jalan.
    courier: (selectedEverproRate as any)?.logistic_name || (selectedEverproRate as any)?.courier_name || (selectedBiteshipRate as any)?.courier_name || '',
    logistic_code: selectedEverproRate?.logistic_code || selectedBiteshipRate?.logistic_code || '',
    rate_code: selectedEverproRate?.rate_code || selectedBiteshipRate?.rate_code || '',
    logistic_logo_url: selectedEverproRate?.logistic_logo_url || selectedBiteshipRate?.logistic_logo_url || '',
    everpro_signed_key: (selectedEverproRate as any)?.signed_key || undefined,
    // Provider pemenang (badge EV/MT/LC) — menentukan modal booking (Everpro vs Mengantar).
    shipping_provider: (selectedEverproRate as any)?.provider || (selectedBiteshipRate ? 'biteship' : 'everpro'),
    everpro_min_duration: (selectedEverproRate as any)?.min_duration ?? undefined,
    everpro_max_duration: (selectedEverproRate as any)?.max_duration ?? undefined,
    paymentMethod: duitkuMethod || canonicalPaymentMethod(String(selectedPaymentKey || '')) || '',
    paymentGateway: duitkuMethod ? 'duitku' : undefined,
    maxShippingForCOD: form?.paymentSettings?.cod?.maxShippingForCOD ?? 30000,
    codShippingLimitEnabled: form?.paymentSettings?.cod?.codShippingLimitEnabled !== false,
    province: cf.province, city: cf.city, district: cf.district, village: cf.village,
    provinceId: (addressData as any).provinceId || '', cityId: (addressData as any).cityId || '',
    districtId: (addressData as any).districtId || '', villageId: (addressData as any).villageId || '',
    shippingAddress: cf.finalShippingAddress, postalCode: (addressData as any).postalCode || '',
    assignedCsId: assignedCsId || null,
    // Sanitasi platform: link FU lama masih bawa utm_source=prospek -> jadi ''.
    // Direct tanpa utm tetap '' (jujur tak diketahui, tampil '-').
    utmSource: canonPlatform(utmParams.utm_source, ''), utmMedium: utmParams.utm_medium || '',
    utmCampaign: utmParams.utm_campaign || '', utmTerm: utmParams.utm_term || '',
    utmContent: utmParams.utm_content || '',
    transferUniqueCode: uniqueCode, quantity,
    // Honeypot anti-bot (dicek server-side di submit_public_order).
    company_website: (customerData as any)?.company_website || '',
    sourceForm: sourceFormOverride ?? ((isSimpleEmbed || cf.isConsult) ? 'embed_simple' : (form?.addressInputMode === 'simple' ? 'embed_simple' : '')),
  };

  return { pOrder, assignedCsId, name: cf.name, email: cf.email, phone: cf.phone, productName: cf.productName, sellingPrice: cf.sellingPrice, brandId: cf.brandId, addressText: cf.addressText, province: cf.province, city: cf.city, district: cf.district, village: cf.village, isCODVal };
}
